import { FC, ReactElement, useState } from "react"
import { Controller, useForm } from "react-hook-form"
import {
  ChallengesService,
  CreateChallengeRequestSchema
} from "../sdk"
import useError from "../hooks/useError"
import classNames from "classnames"
import Button from "../components/Button"
import Input from "../components/Input"
import Col from "../components/Col"
import Checkbox from "../components/Checkbox"
import Textarea from "./Textarea"

type Props = {
  onSuccess?(): void
}

const AddChallengeForm: FC<Props> = ({ onSuccess }) => {
  const [loading, setLoading] = useState<boolean>(false)

  const { control, handleSubmit, formState, getValues } = useForm<CreateChallengeRequestSchema>()
  const { handleError } = useError()

  const onSubmit = async({
    name,
    alias,
    description,
    startTimestamp,
    endTimestamp,
    startSubscriptionTimestamp,
    endSubscriptionTimestamp,
    visibleTimestamp,
    maxPartecipants,
    training,
    individual,
    difficulty,
    private: privateAttr
  }: CreateChallengeRequestSchema): Promise<void> => {
    setLoading(true)
    try {
      await ChallengesService.createChallenge({
        name,
        alias,
        description,
        startTimestamp,
        endTimestamp,
        startSubscriptionTimestamp,
        endSubscriptionTimestamp,
        visibleTimestamp,
        draft: false,
        maxPartecipants: Number(maxPartecipants),
        training,
        individual,
        shortDescription: "",
        htmlDescription: "",
        difficulty: Number(difficulty),
        private: privateAttr
      })

      if (onSuccess) {
        onSuccess()
      }
    } catch (err) {
      handleError(err)
    }
    setLoading(false)
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className={classNames("grid grid-cols-12 gap-3", loading && "opacity-50 pointer-events-none")}>
      <Col lg={6}>
        <Controller
          name="name"
          rules={{ required: "Campo Obbligatorio" }}
          control={control}
          render={({ field: { onChange, value } }): ReactElement => {
            return (
              <Input value={value || ""} onChange={onChange} label="Nome" error={formState.errors.name?.message} />
            )
          }}
        />
      </Col>

      <Col lg={6}>
        <Controller
          name="alias"
          rules={{ required: "Campo Obbligatorio" }}
          control={control}
          render={({ field: { onChange, value } }): ReactElement => {
            return (
              <Input value={value || ""} onChange={onChange} label="Alias" error={formState.errors.alias?.message} />
            )
          }}
        />
      </Col>

      <Col>
        <Controller
          name="description"
          rules={{ required: "Campo Obbligatorio" }}
          control={control}
          render={({ field: { onChange, value } }): ReactElement => {
            return (
              <Textarea value={value || ""} onChange={onChange} label="Descrizione" error={formState.errors.description?.message} />
            )
          }}
        />
      </Col>

      <Col lg={6}>
        <Controller
          name="visibleTimestamp"
          rules={{ required: "Campo Obbligatorio" }}
          control={control}
          render={({ field: { onChange, value } }): ReactElement => {
            return (
              <Input type="datetime-local" value={value || ""} onChange={onChange} label="Data di pubblicazione" error={formState.errors.visibleTimestamp?.message} />
            )
          }}
        />
      </Col>

      <Col lg={6} />

      <Col lg={6}>
        <Controller
          name="startSubscriptionTimestamp"
          rules={{ required: "Campo Obbligatorio" }}
          control={control}
          render={({ field: { onChange, value } }): ReactElement => {
            return (
              <Input type="datetime-local" value={value || ""} onChange={onChange} label="Inizio iscrizioni" error={formState.errors.startSubscriptionTimestamp?.message} />
            )
          }}
        />
      </Col>

      <Col lg={6}>
        <Controller
          name="endSubscriptionTimestamp"
          rules={{ required: "Campo Obbligatorio" }}
          control={control}
          render={({ field: { onChange, value } }): ReactElement => {
            return (
              <Input type="datetime-local" value={value || ""} onChange={onChange} label="Fine iscrizioni" error={formState.errors.endSubscriptionTimestamp?.message} />
            )
          }}
        />
      </Col>

      <Col lg={6}>
        <Controller
          name="startTimestamp"
          control={control}
          render={({ field: { onChange, value } }): ReactElement => {
            return (
              <Input type="datetime-local" value={value || ""} onChange={onChange} label="Inizio" error={formState.errors.startTimestamp?.message} />
            )
          }}
        />
      </Col>

      <Col lg={6}>
        <Controller
          name="endTimestamp"
          control={control}
          render={({ field: { onChange, value } }): ReactElement => {
            return (
              <Input type="datetime-local" value={value || ""} onChange={onChange} label="Fine" error={formState.errors.endTimestamp?.message} />
            )
          }}
        />
      </Col>

      <Col lg={6}>
        <Controller
          name="maxPartecipants"
          rules={{
            required: "Campo Obbligatorio",
            validate: (value: number): boolean | string => {
              const v = +value
              if (v <= 0) {
                return "Valore non valido"
              }
              if (getValues("individual")) {
                return v === 1 || "Nelle gare singole può esserci un solo partecipante"
              }
              return true
            }
          }}
          control={control}
          render={({ field: { onChange, value } }): ReactElement => {
            return (
              <Input value={value} type="number" onChange={onChange} label="Numero massimo di partecipanti" error={formState.errors.maxPartecipants?.message} />
            )
          }}
        />
      </Col>

      <Col lg={6}>
        <Controller
          name="difficulty"
          rules={{
            required: "Campo Obbligatorio",
            validate: (value: number): boolean | string => {
              const v = +value
              return (v >= 1 && v <= 5) || "Il valore deve essere compreso tra 1 e 5"
            }
          }}
          control={control}
          render={({ field: { onChange, value } }): ReactElement => {
            return (
              <Input
                value={value}
                onChange={onChange}
                label="Difficoltà"
                type="number"
                min="1"
                max="5"
                error={formState.errors.difficulty?.message}
              />
            )
          }}
        />
      </Col>

      <Col lg={6}>
        <Controller
          name="training"
          control={control}
          defaultValue={false}
          render={({ field: { onChange, value } }): ReactElement => {
            return (
              <Checkbox
                checked={value}
                onChange={onChange}
                label="Allenamento"
                containerClassName="w-56 flex items-center justify-between"
              />
            )
          }}
        />

        <Controller
          name="individual"
          control={control}
          defaultValue={true}
          render={({ field: { onChange, value } }): ReactElement => {
            return (
              <Checkbox
                checked={value}
                onChange={onChange}
                label="Individuale"
                containerClassName="w-56 flex items-center justify-between"
              />
            )
          }}
        />

        <Controller
          name="private"
          control={control}
          defaultValue={false}
          render={({ field: { onChange, value } }): ReactElement => {
            return (
              <Checkbox
                checked={value}
                onChange={onChange}
                label="Privata"
                containerClassName="w-56 flex items-center justify-between"
              />
            )
          }}
        />
      </Col>

      <Col>
        <Button type="submit" className="w-full">Salva</Button>
      </Col>
    </form>
  )
}

export default AddChallengeForm
